import React, { useEffect } from "react";
import { endOfMonth, parseISO, startOfMonth } from "date-fns";
import { isEmpty } from "lodash";
import { useState } from "react";
import { BookingType, PricingType, Space } from "../../../data";
import { toDay } from "../../../lib/datetime";
import { AvailableDay } from "../../../offering/api";
import {
  useBestDayRatesQuery,
  getViewRange,
} from "../../hooks/use_best_day_rates_query";
import { useGetBetterHourRatesQuery } from "../../hooks/use_better_hour_rates_query";
import { toBestDateRates } from "../../utils/to_best_date_rates";
import { CalendarSwitch } from "../calendar_switch";
import { TimeRangePicker, TimeSlot } from "./checkout_datetime_picker";
import { getDurationInHours, Time } from "../../../offering/time_utils";
import { useAnalytics } from "../../../lib/analytics/analytics";
import { Day } from "../../../offering/day_utils";
import {
  DateRangePicker,
  PickingRange,
} from "../date_range_picker/date_range_picker";
import {
  useDurationPickerFeatureFlag,
  usePriceBreakdownPerHourFeatureFlag,
} from "@/core/splitio";
import { DurationRangePicker } from "./duration_range_picker";
import { colors } from "../colors";
import { useI18n } from "@/lib/i18n/i18n_provider";

export interface HourlyValue {
  date?: Day;
  startTime?: Time;
  endTime?: Time;
}

export interface DailyValue {
  startDate?: Day;
  endDate?: Day;
}

export type DateTimeValue = HourlyValue | DailyValue;

export const isHourlyValue = (value?: any): value is HourlyValue =>
  !!value && "date" in value;

interface DateTimePickerProps<T extends DateTimeValue> {
  switchValue: BookingType;
  space?: Space | null;
  timeslots: TimeSlot[];
  availableDays?: AvailableDay[];
  onChangeMonth: (startDate: Date, endDate: Date) => void;
  onSwitch: (value: BookingType) => void;
  hideSwitch?: boolean;
  value?: T;
  onChange: (value: T) => void;
  pickingRange: PickingRange;
  spaceLayoutId?: string;
}

export function DateTimePicker<T extends DateTimeValue>({
  switchValue,
  space,
  timeslots,
  availableDays,
  onChangeMonth,
  onSwitch,
  hideSwitch,
  value,
  onChange,
  pickingRange,
  spaceLayoutId,
}: DateTimePickerProps<T>) {
  const { fbt } = useI18n();

  const durationPickerFeatureFlag = useDurationPickerFeatureFlag();
  const priceBeakdownPerHourFeatureFlag = usePriceBreakdownPerHourFeatureFlag();

  const dateValue =
    isHourlyValue(value) && !!value.date
      ? {
          startDate: parseISO(value.date),
          endDate: parseISO(value.date),
        }
      : !isHourlyValue(value)
      ? {
          startDate: value?.startDate ? parseISO(value.startDate) : undefined,
          endDate: value?.endDate ? parseISO(value.endDate) : undefined,
        }
      : {};

  const analytics = useAnalytics();

  const isHourlySwitch = switchValue === BookingType.Hourly;

  const [viewingRange, setViewingRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>();

  const { data: bestDayRates } = useBestDayRatesQuery(
    getViewRange(viewingRange, {
      startDate: isHourlyValue(value) ? value?.date : value?.startDate,
      endDate: isHourlyValue(value) ? value?.date : value?.endDate,
    }),
    isHourlySwitch ? BookingType.Hourly : BookingType.Daily,
    space?.currencyCode,
    space?.id,
    spaceLayoutId
  );

  const pricings = spaceLayoutId
    ? space?.layouts.find((l) => l.id === spaceLayoutId)?.pricings
    : space?.pricings;
  const basePrice = pricings?.find((p) =>
    isHourlySwitch
      ? p.type === PricingType.Hourly
      : p.type === PricingType.Daily
  )?.price;
  const hourlyUnitPrice =
    isHourlyValue(value) && value.date
      ? bestDayRates.options[value.date] || basePrice
      : undefined;

  const betterHourRates = useGetBetterHourRatesQuery(
    timeslots,
    hourlyUnitPrice,
    isHourlyValue(value) ? value : undefined,
    space?.id,
    space?.currencyCode,
    spaceLayoutId
  );

  const handleChangeDate = (value: { startDate?: Date; endDate?: Date }) => {
    if (isHourlySwitch) {
      onChange({ date: value.startDate && toDay(value.startDate) } as T);
    } else {
      onChange({
        startDate: value.startDate && toDay(value.startDate),
        endDate: value.endDate && toDay(value.endDate),
      } as T);
    }
  };

  useEffect(() => {
    if (isHourlyValue(value) && betterHourRates.recommendation) {
      const { date, startTime, endTime } = value || {};
      if (date && startTime && endTime) {
        const duration = getDurationInHours(startTime, endTime);
        analytics.event(
          "Date Select DP Display",
          {
            "Purchased Products": {
              item_id: space?.id,
              item_name: space?.name,
              quantity: duration,
              price: basePrice,
              discount: betterHourRates.dynamicPrice?.[date],
            },
            Date: date,
            "Start time": startTime,
            "End Time": endTime,
            Duration: duration,
            "Discount Display": betterHourRates.recommendation,
            Source: "WL",
          },
          { onlyProviders: ["mixpanel", "customerio"] }
        );
      }
    }
  }, [
    betterHourRates.recommendation,
    value,
    space?.id,
    space?.name,
    analytics,
    basePrice,
    betterHourRates.dynamicPrice,
  ]);

  const dynamicPricings = toBestDateRates(
    bestDayRates.options,
    betterHourRates.dynamicPrice,
    space?.currencyCode
  );

  const availableDates = isHourlySwitch
    ? availableDays?.map((a) => parseISO(a.date))
    : availableDays
        ?.filter((aD) => aD.availableFullDay)
        .map((aD) => parseISO(aD.date));

  const handleViewDates = (
    startDate: Date,
    endDate: Date,
    viewingDate: Date
  ) => {
    onChangeMonth(startDate, endDate);
    setViewingRange({
      startDate: startOfMonth(viewingDate),
      endDate: endOfMonth(viewingDate),
    });
  };

  return (
    <div>
      {!hideSwitch && (
        <div className="px-4 py-3 border-bottom">
          <CalendarSwitch value={switchValue} onChange={onSwitch} />
        </div>
      )}
      <div>
        <DateRangePicker
          availableDates={availableDates}
          basePrice={basePrice}
          currency={space?.currencyCode || undefined}
          dynamicPricings={dynamicPricings}
          hidePrice={
            isEmpty(space?.dynamicPriceRates) && !space?.smartPricingEnabled
          }
          onChange={handleChangeDate}
          onViewDates={handleViewDates}
          value={dateValue}
          pickingRange={pickingRange}
          partnerId={space?.partnerID}
          displayPlus={
            priceBeakdownPerHourFeatureFlag &&
            isHourlySwitch &&
            !!(space?.priceRanges.hourly?.lowestPrice && basePrice) &&
            space.priceRanges.hourly.lowestPrice < basePrice
          }
        />
        {isHourlySwitch && (
          <div>
            {durationPickerFeatureFlag && !dateValue.startDate && space ? (
              <div
                className="small text-center m-3 small-price-text"
                style={{ color: colors.blackMinus30 }}
              >
                {fbt("Prices vary by day. Select a date to choose a time.")}
              </div>
            ) : !!dateValue.startDate && !betterHourRates.timeSlots.length ? (
              <div
                className="small text-center m-3 small-availability-text"
                style={{ color: colors.blackMinus30 }}
              >
                {fbt("Checking available start times...")}
              </div>
            ) : null}
            {betterHourRates.recommendation && (
              <div className="text-success small text-center m-3">
                {betterHourRates.recommendation}
              </div>
            )}
            <div
              data-testid="time-range-picker-container"
              className="time-range-picker d-flex justify-content-between m-3"
            >
              {durationPickerFeatureFlag ? (
                <DurationRangePicker
                  onChangeTime={(timeValue) => {
                    onChange({
                      startTime: timeValue.start,
                      endTime: timeValue.end,
                    } as T);
                  }}
                  timeslots={betterHourRates.timeSlots}
                  value={{
                    start: isHourlyValue(value) ? value?.startTime : undefined,
                    end: isHourlyValue(value) ? value?.endTime : undefined,
                  }}
                  spaceId={space?.id}
                  date={dateValue.startDate && toDay(dateValue.startDate)}
                  unitPrice={hourlyUnitPrice}
                  currency={space?.currencyCode || undefined}
                />
              ) : (
                <TimeRangePicker
                  onChangeTime={(timeValue) => {
                    onChange({
                      startTime: timeValue.start,
                      endTime: timeValue.end,
                    } as T);
                  }}
                  timeslots={betterHourRates.timeSlots}
                  value={{
                    start: isHourlyValue(value) ? value?.startTime : undefined,
                    end: isHourlyValue(value) ? value?.endTime : undefined,
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
